import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  PackageIcon, TruckIcon, LockIcon, CheckIcon,
  CreditCardIcon, ClockIcon, MapPinIcon, ShieldIcon,
} from '../../components/Icons';
import { DocLink } from '../../components/DocModal';

const BASE  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const token = () => localStorage.getItem('kialuga_token');

export default function Checkout() {
  const navigate        = useNavigate();
  const { utilizador }  = useAuth();
  const [dados,         setDados]        = useState(null);
  const [morada,        setMorada]       = useState('');
  const [notas,         setNotas]        = useState('');
  const [metodo,        setMetodo]       = useState('multicaixa');
  const [modoEntrega,   setModoEntrega]  = useState('entrega');
  const [loading,       setLoading]      = useState(false);
  const [erro,          setErro]         = useState('');
  const [sucesso,       setSucesso]      = useState('');
  const [reservaId,     setReservaId]    = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('kialuga_checkout');
    if (!raw) { navigate('/cliente'); return; }
    try {
      const d = JSON.parse(raw);
      setDados(d);
      // Pre-selecionar modo baseado na opção do anúncio
      if (d.opcao_entrega === 'levantamento') setModoEntrega('levantamento');
      else setModoEntrega('entrega');
    }
    catch { navigate('/cliente'); }
  }, []);

  const confirmar = async () => {
    if (!dados) return;
    setLoading(true); setErro('');
    try {
      const res = await fetch(`${BASE}/reservas`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          anuncio_id:       dados.anuncio_id,
          tipo:             dados.tipo || 'aluguer',
          data_inicio:      dados.data_inicio,
          data_fim:         dados.data_fim,
          morada_entrega:   modoEntrega === 'entrega' ? morada : null,
          notas,
          metodo_pagamento: metodo,
          modo_entrega:     modoEntrega,
        }),
      });
      const data = await res.json();
      if (!data.sucesso) throw new Error(data.mensagem || 'Erro ao criar reserva');
      setReservaId(data.reserva.id);
      setSucesso('Reserva criada com sucesso!');
      sessionStorage.removeItem('kialuga_checkout');
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!dados) return null;

  // Bloquear utilizadores não verificados — linha de defesa no frontend
  if (utilizador && utilizador.kyc_estado !== 'verificado') {
    return (
      <>
        <div className="topbar">
          <div><div className="topbar-title">Reserva bloqueada</div></div>
          <div className="topbar-actions">
            <button className="btn btn-outline" onClick={() => navigate(-1)}>← Voltar</button>
          </div>
        </div>
        <div className="page-content" style={{ maxWidth: '480px', margin: '0 auto', paddingTop: '40px' }}>
          <div className="card" style={{ textAlign: 'center', padding: '40px 32px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <ShieldIcon size={28} color='var(--warning)' />
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>Verificação necessária</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '24px' }}>
              Para fazer reservas na KiAluga, a tua identidade tem de estar verificada.<br />
              Submete o teu BI e uma selfie na página de perfil. O processo é rápido e protege toda a comunidade.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button className="btn btn-outline" onClick={() => navigate('/cliente')}>← Explorar</button>
              <button className="btn btn-primary" onClick={() => navigate('/perfil')}>Verificar identidade →</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const foto    = dados.fotos?.[0] || null;
  const metodos = [
    { id:'multicaixa', label:'Multicaixa Express', icon:<CreditCardIcon size={20} color='#2563EB'/>, desc:'Pagamento via referência multicaixa' },
  ];

  if (sucesso && reservaId) return (
    <>
      <div className="topbar">
        <div><div className="topbar-title">{dados?.tipo === 'compra' ? 'Compra confirmada!' : 'Reserva confirmada!'}</div></div>
      </div>
      <div className="page-content" style={{maxWidth:'500px',margin:'0 auto',textAlign:'center',paddingTop:'40px'}}>
        <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'40px 32px'}}>
          <div style={{width:'64px',height:'64px',borderRadius:'50%',background:'var(--success-bg)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
            <CheckIcon size={28} color='var(--success)'/>
          </div>
          <div style={{fontSize:'20px',fontWeight:'700',marginBottom:'8px'}}>
            {dados?.tipo === 'compra' ? '🛒 Compra confirmada!' : '✅ Reserva confirmada!'}
          </div>
          <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'20px',lineHeight:'1.6'}}>
            {dados?.tipo === 'compra'
              ? <>A tua compra de <strong>{dados.titulo}</strong> foi registada. O fornecedor irá contactar-te para a entrega.</>
              : <>A tua reserva de <strong>{dados.titulo}</strong> foi criada com sucesso. O fornecedor foi notificado.</>
            }
          </div>
          {dados?.tipo !== 'compra' && (
            <div style={{background:'var(--info-bg)',border:'1px solid #bfdbfe',borderRadius:'8px',padding:'12px 16px',marginBottom:'24px',fontSize:'12px',color:'var(--info)',lineHeight:'1.6',textAlign:'left',display:'flex',gap:'8px',alignItems:'flex-start'}}>
              <LockIcon size={14} color='var(--info)' style={{flexShrink:0,marginTop:'1px'}}/>
              <span>O teu pagamento está protegido por escrow. O fornecedor só recebe o valor do aluguer quando confirmar a entrega. A caução é devolvida após devolução sem problemas.</span>
            </div>
          )}
          {dados?.tipo === 'compra' && (
            <div style={{background:'var(--success-bg)',border:'1px solid var(--success-border)',borderRadius:'8px',padding:'12px 16px',marginBottom:'24px',fontSize:'12px',color:'var(--success)',lineHeight:'1.6',textAlign:'left',display:'flex',gap:'8px',alignItems:'flex-start'}}>
              <CheckIcon size={14} color='var(--success)' style={{flexShrink:0,marginTop:'1px'}}/>
              <span>Compra registada! O fornecedor receberá a tua morada para entrega. Pagamento processado com segurança pela KiAluga.</span>
            </div>
          )}
          <div style={{display:'flex',gap:'8px',justifyContent:'center'}}>
            <button className="btn btn-outline" onClick={()=>navigate('/cliente/reservas')}>Ver reservas</button>
            <button className="btn btn-primary" onClick={()=>navigate('/cliente')}>Explorar mais →</button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Confirmar {dados?.tipo === 'compra' ? 'compra' : 'reserva'}</div>
          <div className="topbar-sub">{dados?.tipo === 'compra' ? 'Revê os detalhes da compra antes de confirmar' : 'Revê os detalhes antes de confirmar'}</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-outline" onClick={()=>navigate(-1)}>← Voltar</button>
        </div>
      </div>

      <div className="page-content">
        <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:'18px',alignItems:'start',maxWidth:'900px'}}>

          {/* Esquerda */}
          <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>

            {/* Produto */}
            <div className="card">
              <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',fontWeight:'600',marginBottom:'12px'}}>
                <PackageIcon size={15} color='var(--orange)'/> Produto reservado
              </div>
              <div style={{display:'flex',gap:'12px',alignItems:'center'}}>
                <div style={{width:'72px',height:'72px',borderRadius:'10px',overflow:'hidden',flexShrink:0,background:'linear-gradient(135deg,#fde8d8,#fed7b0)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {foto
                    ? <img src={foto} alt={dados.titulo} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'} />
                    : <PackageIcon size={28} color='rgba(249,115,22,0.5)'/>
                  }
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:'15px',fontWeight:'600',marginBottom:'2px'}}>{dados.titulo}</div>
                  <div style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'12px',color:'var(--text-muted)',marginBottom:'4px'}}>
                    <MapPinIcon size={11}/> {dados.localizacao||'Luanda'}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',color:'var(--text-muted)'}}>
                    {dados.fornecedor_foto
                      ? <img src={dados.fornecedor_foto} alt="" style={{width:'18px',height:'18px',borderRadius:'50%',objectFit:'cover'}} onError={e=>e.target.style.display='none'} />
                      : <div style={{width:'18px',height:'18px',borderRadius:'50%',background:'var(--orange)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'9px',color:'#fff',fontWeight:'700'}}>{(dados.fornecedor_nome||'?')[0]}</div>
                    }
                    {dados.fornecedor_nome||'Fornecedor'}
                  </div>
                </div>
              </div>
              {dados.tipo === 'compra' ? (
                <div style={{display:'flex',justifyContent:'center',marginTop:'12px',paddingTop:'12px',borderTop:'1px solid var(--border)'}}>
                  <span style={{background:'var(--success-bg)',border:'1px solid var(--success-border)',color:'var(--success)',padding:'6px 16px',borderRadius:'20px',fontSize:'12px',fontWeight:'600'}}>
                    🛒 Compra à vista — entrega acordada com o fornecedor
                  </span>
                </div>
              ) : (
                <div className="grid-3" style={{gap:'8px',marginTop:'12px',paddingTop:'12px',borderTop:'1px solid var(--border)'}}>
                  {[['Início',new Date(dados.data_inicio).toLocaleDateString('pt-PT')],['Fim',new Date(dados.data_fim).toLocaleDateString('pt-PT')],['Duração',`${dados.dias} dia${dados.dias>1?'s':''}`]].map(([l,v])=>(
                    <div key={l} style={{background:'var(--surface)',borderRadius:'8px',padding:'8px 10px',textAlign:'center'}}>
                      <div style={{fontSize:'10px',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:'2px'}}>{l}</div>
                      <div style={{fontSize:'13px',fontWeight:'600'}}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modo de receção */}
            {dados.tipo !== 'compra' && (
            <div className="card">
              <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',fontWeight:'600',marginBottom:'12px'}}>
                <TruckIcon size={15} color='var(--orange)'/> Modo de receção
              </div>
              <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
                {(dados.opcao_entrega === 'entrega' || dados.opcao_entrega === 'ambas') && (
                  <div onClick={()=>setModoEntrega('entrega')}
                    style={{flex:1,padding:'10px 12px',borderRadius:'10px',cursor:'pointer',border:modoEntrega==='entrega'?'1.5px solid var(--orange)':'1.5px solid var(--border)',background:modoEntrega==='entrega'?'var(--orange-light)':'transparent',textAlign:'center'}}>
                    <TruckIcon size={16} color={modoEntrega==='entrega'?'var(--orange)':'var(--text-muted)'}/>
                    <div style={{fontSize:'12px',fontWeight:'600',marginTop:'4px',color:modoEntrega==='entrega'?'var(--orange-dark)':'var(--text-primary)'}}>Entrega</div>
                    <div style={{fontSize:'10px',color:'var(--text-muted)'}}>Fornecedor entrega</div>
                  </div>
                )}
                {(dados.opcao_entrega === 'levantamento' || dados.opcao_entrega === 'ambas') && (
                  <div onClick={()=>setModoEntrega('levantamento')}
                    style={{flex:1,padding:'10px 12px',borderRadius:'10px',cursor:'pointer',border:modoEntrega==='levantamento'?'1.5px solid var(--orange)':'1.5px solid var(--border)',background:modoEntrega==='levantamento'?'var(--orange-light)':'transparent',textAlign:'center'}}>
                    <MapPinIcon size={16} color={modoEntrega==='levantamento'?'var(--orange)':'var(--text-muted)'}/>
                    <div style={{fontSize:'12px',fontWeight:'600',marginTop:'4px',color:modoEntrega==='levantamento'?'var(--orange-dark)':'var(--text-primary)'}}>Levantamento</div>
                    <div style={{fontSize:'10px',color:'var(--text-muted)'}}>Vai buscar</div>
                  </div>
                )}
              </div>

              {modoEntrega === 'entrega' && (
                <div style={{marginBottom:'12px'}}>
                  <div style={{fontSize:'12px',fontWeight:'500',color:'var(--text-secondary)',marginBottom:'5px'}}>Morada de entrega</div>
                  <input type="text" placeholder="Rua, bairro, município..." value={morada} onChange={e=>setMorada(e.target.value)}
                    style={{width:'100%',padding:'9px 12px',border:'1px solid var(--border)',borderRadius:'8px',fontSize:'13px',fontFamily:'inherit',outline:'none',background:'var(--card)',color:'var(--text-primary)'}} />
                </div>
              )}
              {modoEntrega === 'levantamento' && (
                <div style={{background:'var(--info-bg)',border:'1px solid #bfdbfe',borderRadius:'8px',padding:'10px 12px',marginBottom:'12px',fontSize:'12px',color:'var(--info)',lineHeight:'1.5'}}>
                  O endereço de levantamento será mostrado após o fornecedor confirmar a reserva.
                </div>
              )}

              <div>
                <div style={{fontSize:'12px',fontWeight:'500',color:'var(--text-secondary)',marginBottom:'5px'}}>Notas para o fornecedor</div>
                <textarea placeholder="Ex: Contactar antes de entregar..." value={notas} onChange={e=>setNotas(e.target.value)} rows={2}
                  style={{width:'100%',padding:'9px 12px',border:'1px solid var(--border)',borderRadius:'8px',fontSize:'13px',fontFamily:'inherit',outline:'none',background:'var(--card)',color:'var(--text-primary)',resize:'vertical'}} />
              </div>
            </div>
            )}

            {/* Método pagamento */}
            <div className="card">
              <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',fontWeight:'600',marginBottom:'12px'}}>
                <CreditCardIcon size={15} color='var(--orange)'/> Método de pagamento
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {metodos.map(m=>(
                  <div key={m.id} onClick={()=>setMetodo(m.id)} style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px',borderRadius:'10px',cursor:'pointer',transition:'all 0.15s',border:metodo===m.id?'1.5px solid var(--orange)':'1.5px solid var(--border)',background:metodo===m.id?'var(--orange-light)':'transparent'}}>
                    {m.icon}
                    <div style={{flex:1}}>
                      <div style={{fontSize:'13px',fontWeight:'500',color:metodo===m.id?'var(--orange-dark)':'var(--text-primary)'}}>{m.label}</div>
                      <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{m.desc}</div>
                    </div>
                    <div style={{width:'16px',height:'16px',borderRadius:'50%',border:`2px solid ${metodo===m.id?'var(--orange)':'var(--border)'}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {metodo===m.id && <div style={{width:'8px',height:'8px',borderRadius:'50%',background:'var(--orange)'}}></div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Resumo */}
          <div style={{position:'sticky',top:'80px'}}>
            <div className="card">
              <div style={{fontSize:'14px',fontWeight:'600',marginBottom:'14px'}}>Resumo do pagamento</div>
              {(dados.tipo === 'compra' ? [
                ['Preço de compra', `${(dados.valor_aluguer||0).toLocaleString()} Kz`],
              ] : [
                [`Aluguer (${dados.dias} dia${dados.dias>1?'s':''})`, `${(dados.valor_aluguer||0).toLocaleString()} Kz`],
                ['Caução (reembolsável)', `${(dados.valor_caucao||0).toLocaleString()} Kz`],
              ]).map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',fontSize:'12px',padding:'5px 0',borderBottom:'1px solid var(--border)'}}>
                  <span style={{color:'var(--text-secondary)'}}>{l}</span><span>{v}</span>
                </div>
              ))}
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'15px',fontWeight:'700',paddingTop:'10px',marginTop:'4px'}}>
                <span>Total</span>
                <span style={{color:'var(--orange)'}}>{(dados.valor_total||0).toLocaleString()} Kz</span>
              </div>

              <div style={{background:'var(--info-bg)',border:'1px solid #bfdbfe',borderRadius:'8px',padding:'10px 12px',margin:'14px 0',fontSize:'11px',color:'var(--info)',lineHeight:'1.6',display:'flex',gap:'7px',alignItems:'flex-start'}}>
                <LockIcon size={12} color='var(--info)' style={{flexShrink:0,marginTop:'1px'}}/>
                <span>Pagamento protegido por escrow. O fornecedor só recebe na entrega confirmada. <DocLink docKey="escrow" style={{fontSize:'11px'}}>Saber mais</DocLink></span>
              </div>

              {dados.tipo !== 'compra' && (
                <div style={{fontSize:'11px',color:'var(--text-muted)',lineHeight:'1.6',marginBottom:'10px'}}>
                  A <DocLink docKey="caucao" style={{fontSize:'11px'}}>caução ({(dados.valor_caucao||0).toLocaleString()} Kz)</DocLink> é reembolsável após devolução sem incidentes.
                  Ver <DocLink docKey="cancelamento" style={{fontSize:'11px'}}>política de cancelamento</DocLink>.
                </div>
              )}

              {erro    && <div style={{background:'var(--danger-bg)',border:'1px solid var(--danger-border)',borderRadius:'8px',padding:'10px 12px',marginBottom:'10px',fontSize:'12px',color:'var(--danger)'}}>{erro}</div>}
              {sucesso && <div style={{background:'var(--success-bg)',border:'1px solid var(--success-border)',borderRadius:'8px',padding:'10px 12px',marginBottom:'10px',fontSize:'12px',color:'var(--success)'}}>{sucesso}</div>}

              <button className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:'12px',fontSize:'14px',display:'flex',alignItems:'center',gap:'7px'}} onClick={confirmar} disabled={loading}>
                {loading
                  ? <><ClockIcon size={14}/> A processar...</>
                  : <><LockIcon size={14}/> Confirmar e pagar {(dados.valor_total||0).toLocaleString()} Kz</>
                }
              </button>
              <div style={{textAlign:'center',fontSize:'11px',color:'var(--text-muted)',marginTop:'8px',lineHeight:'1.6'}}>
                Ao confirmar aceitas os <DocLink docKey="termos" style={{fontSize:'11px'}}>Termos de Uso</DocLink> e a{' '}
                <DocLink docKey="privacidade" style={{fontSize:'11px'}}>Política de Privacidade</DocLink> da KiAluga.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
